/**
 * UserStatus constants and utilities — §21 string model.
 *
 * Values stored in DB: `"system:N"` for built-in statuses, `uuid` for custom.
 * v1 legacy: plain numbers (1–14) — use `normalizeUserStatus()` to handle.
 */

// ---------------------------------------------------------------------------
// System enum (matches v1 UserStatus exactly)
// ---------------------------------------------------------------------------

export enum UserStatus {
  Idea = 1,
  Ready = 2,
  InProgress = 3,
  Waiting = 4,
  Blocked = 5,
  Review = 6,
  Testing = 7,
  Approved = 8,
  Deployed = 9,
  Done = 10,
  OnHold = 11,
  Deferred = 12,
  Rejected = 13,
  Cancelled = 14,
}

// ---------------------------------------------------------------------------
// Colors — matches v1 `userStatusColors` (same hex values)
// ---------------------------------------------------------------------------

export const USER_STATUS_COLORS: Record<UserStatus, string> = {
  [UserStatus.Idea]: "#fff3cd",
  [UserStatus.Ready]: "#ffc107",
  [UserStatus.InProgress]: "#007BFF",
  [UserStatus.Waiting]: "#6f42c1",
  [UserStatus.Blocked]: "#DC3545",
  [UserStatus.Review]: "#FD7E14",
  [UserStatus.Testing]: "#17a2b8",
  [UserStatus.Approved]: "#28a745",
  [UserStatus.Deployed]: "#218838",
  [UserStatus.Done]: "#006400",
  [UserStatus.OnHold]: "#6c757d",
  [UserStatus.Deferred]: "#eecdd1",
  [UserStatus.Rejected]: "#721c24",
  [UserStatus.Cancelled]: "#343a40",
}

// ---------------------------------------------------------------------------
// Display order — matches v1 `userStatusOrder`
// ---------------------------------------------------------------------------

export const USER_STATUS_ORDER: UserStatus[] = [
  UserStatus.Idea,
  UserStatus.Ready,
  UserStatus.InProgress,
  UserStatus.Waiting,
  UserStatus.Blocked,
  UserStatus.Review,
  UserStatus.Testing,
  UserStatus.Approved,
  UserStatus.Deployed,
  UserStatus.Done,
  UserStatus.OnHold,
  UserStatus.Deferred,
  UserStatus.Rejected,
  UserStatus.Cancelled,
]

// ---------------------------------------------------------------------------
// Custom UserStatus interface (Clone & Own model, §21)
// ---------------------------------------------------------------------------

export interface CustomUserStatus {
  /** Unique ID — `"system:N"` for cloned defaults, uuid for new custom statuses */
  id: string
  /** Original enum value if cloned from system default */
  baseEnum?: UserStatus
  /** Display label, e.g. "In Progress" or a custom name */
  label: string
  /** Hex color */
  color: string
  /** Sort order (fractional) */
  position: number
  /** false = soft-deleted, kept for historical reference */
  isActive: boolean
}

// ---------------------------------------------------------------------------
// String ↔ enum conversion
// ---------------------------------------------------------------------------

/** Convert a UserStatus enum value to its DB string form: `"system:3"` */
export function toUserStatusString(enumValue: UserStatus): string {
  return `system:${enumValue}`
}

/** Parse a `"system:N"` string and return the enum value, or `null` if not a system status */
export function parseSystemStatusId(value: string): UserStatus | null {
  if (!value.startsWith("system:")) return null
  const num = Number(value.slice(7))
  if (Number.isNaN(num) || !UserStatus[num]) return null
  return num as UserStatus
}

/**
 * Normalize any `userStatus` value to the v2 string form.
 * Handles: number (v1 legacy), string (v2), null/undefined → undefined.
 */
export function normalizeUserStatus(value: unknown): string | undefined {
  if (value == null) return undefined
  if (typeof value === "number") {
    if (value === 0) return undefined // 0 = no status (legacy default)
    return `system:${value}`
  }
  if (typeof value === "string") {
    if (value === "" || value === "0") return undefined
    return value
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Human-readable label
// ---------------------------------------------------------------------------

/** Convert a UserStatus enum value to a display label: `UserStatus.InProgress → "In Progress"` */
export function formatUserStatus(enumValue: UserStatus): string {
  const key = UserStatus[enumValue] as string | undefined
  if (!key) return ""
  return key
    .split(/(?=[A-Z])/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
}

// ---------------------------------------------------------------------------
// Display resolution — works with both system defaults and custom statuses
// ---------------------------------------------------------------------------

export interface UserStatusDisplay {
  label: string
  color: string
  isActive: boolean
}

/**
 * Resolve display info for a `userStatus` value.
 *
 * 1. If `customStatuses` are provided, looks up by ID there.
 * 2. Otherwise falls back to system defaults.
 */
export function getUserStatusDisplay(
  value: string | number | undefined | null,
  customStatuses?: CustomUserStatus[],
): UserStatusDisplay {
  const normalized = normalizeUserStatus(value)
  if (!normalized) {
    return { label: "", color: "#9ca3af", isActive: false }
  }

  // Custom statuses take precedence
  if (customStatuses?.length) {
    const custom = customStatuses.find((s) => s.id === normalized)
    if (custom) {
      return {
        label: custom.label,
        color: custom.isActive ? custom.color : "#9ca3af",
        isActive: custom.isActive,
      }
    }
    // Not found in custom array — removed or orphaned
    return { label: "Unknown", color: "#9ca3af", isActive: false }
  }

  // System default lookup
  const enumValue = parseSystemStatusId(normalized)
  if (enumValue !== null) {
    return {
      label: formatUserStatus(enumValue),
      color: USER_STATUS_COLORS[enumValue],
      isActive: true,
    }
  }

  return { label: "Unknown", color: "#9ca3af", isActive: false }
}

// ---------------------------------------------------------------------------
// Clone system defaults → CustomUserStatus[] (for "Customize" flow)
// ---------------------------------------------------------------------------

export function cloneSystemDefaults(): CustomUserStatus[] {
  return USER_STATUS_ORDER.map((enumValue, i) => ({
    id: `system:${enumValue}`,
    baseEnum: enumValue,
    label: formatUserStatus(enumValue),
    color: USER_STATUS_COLORS[enumValue],
    position: (i + 1) * 1000,
    isActive: true,
  }))
}

// ---------------------------------------------------------------------------
// System status items for the picker (when no custom statuses exist)
// ---------------------------------------------------------------------------

export interface SystemStatusItem {
  id: string
  label: string
  color: string
  enumValue: UserStatus
}

export function getSystemStatusItems(): SystemStatusItem[] {
  return USER_STATUS_ORDER.map((enumValue) => ({
    id: `system:${enumValue}`,
    label: formatUserStatus(enumValue),
    color: USER_STATUS_COLORS[enumValue],
    enumValue,
  }))
}
